import { create } from 'zustand';
import { supabase } from '../supabase';
import { useAuthStore } from './useAuthStore';
import { useOfflineQueue } from './useOfflineQueue';
import { usePieceStore } from './usePieceStore';

// Production-grade: All logic in database RPC functions
// No Edge Functions - everything uses RPC

interface InlineQCState {
    loading: boolean;
    error: string | null;
    
    internalQCPiece: (sampleId: string, pieceId: string, passed: boolean, defects?: Record<string, Record<string, number>>, notes?: string, date?: string) => Promise<void>;
    inlineQCBulk: (qcResults: Array<{pieceId: string, passed: boolean, defects?: any}>) => Promise<void>;
}

export const useInlineQCStore = create<InlineQCState>((set) => ({
    loading: false,
    error: null,

    inlineQCBulk: async (qcResults) => {
        if (!navigator.onLine) {
            await useOfflineQueue.getState().addToQueue('inlineQCBulk', { qcResults });
            return;
        }
        const u = useAuthStore.getState().userProfile;
        if (!u?.factory_id) return;

        set({ loading: true, error: null });
        try {
            const idempotencyKey = `inline_qc_${Date.now()}`;
            const qcJson = qcResults.map(r => ({
                piece_id: r.pieceId,
                passed: r.passed,
                defects: r.defects || []
            }));

            const { data, error } = await supabase.rpc('rpc_inline_qc_bulk', {
                p_factory_id: u.factory_id,
                p_qc_results: qcJson,
                p_user_id: u.id,
                p_user_role: u.role,
                p_qc_at: new Date().toISOString(),
                p_idempotency_key: idempotencyKey
            });

            if (error) throw error;
            if (!data.success) throw new Error(data.error_message);

            console.log(`✅ Inline QC completed: ${data.affected_count} pieces`);
            await usePieceStore.getState().fetchAssignedSamples();

        } catch (e: any) {
            console.error('❌ inlineQCBulk:', e);
            set({ error: e.message });
            throw e;
        } finally {
            set({ loading: false });
        }
    },

    internalQCPiece: async (_sampleId, pieceId, passed, defects, notes, date) => {
        // Single-piece wrapper - uses bulk RPC internally
        const defectsArr = passed ? [] : (defects ? Object.entries(defects).flatMap(([cat, items]) =>
            Object.entries(items as Record<string, number>).map(([item, count]) => ({
                category: cat,
                item: item,
                count: count
            }))
        ) : []);
        
        const { inlineQCBulk } = useInlineQCStore.getState();
        await inlineQCBulk([{ pieceId, passed, defects: defectsArr }]);
    },
}));
